import React from 'react';

import classNames from 'classnames';

import { COLUMN_DEFINITIONS, formatOverviewPercent, OverviewMoney } from './timeOverviewFormat';
import TimeOverviewInfoTip from './TimeOverviewInfoTip';

import Badge from 'CommonComponents/Badge';
import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

/**
 * The figure columns in on-screen order; `key` is both the payload key and the
 * sort the server accepts. Charged opens the recorded half of the table, so it
 * carries the divider from the budgeted half.
 */
const FIGURE_COLUMNS = ['budget', 'cost', 'budgeted_margin', 'charged', 'cost_to_date', 'current_margin', 'variance'];

const RECORDED_DIVIDER = 'charged';

const PERCENT_KEYS = {
    budgeted_margin: 'budgeted_margin_percent',
    current_margin: 'current_margin_percent',
};

/** Tones for a body row and for the total row, which sits on the blue footer. */
const TONES = {
    body: { negative: 'tw-text-red', percent: 'tw-text-charcoal-60', divider: 'tw-border-s-4 tw-border-s-blue-25' },
    total: { negative: 'tw-text-red-25', percent: 'tw-text-blue-15', divider: 'tw-border-s-4 tw-border-s-blue-50' },
};

function SortHeader({ columnKey, label, sort, direction, onSort, numeric, children }) {
    const active = sort === columnKey;

    return (
        <th
            className={classNames(numeric ? 'tw-text-end' : 'tw-text-start', 'tw-whitespace-nowrap', {
                'tw-border-s-4 tw-border-s-white': columnKey === RECORDED_DIVIDER,
            })}
            aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <div className={classNames('tw-flex tw-items-center tw-gap-x-1.5', { 'tw-justify-end': numeric })}>
                <button type="button" className="tw-font-semibold" onClick={() => onSort(columnKey)}>
                    <TableSortHeaderCell asc={active && direction === 'asc'} desc={active && direction === 'desc'}>
                        {label}
                    </TableSortHeaderCell>
                </button>
                {children}
            </div>
        </th>
    );
}

function FigureCell({ figures, columnKey, tone }) {
    const tones = TONES[tone];
    const value = figures[columnKey];
    const percent = PERCENT_KEYS[columnKey] ? formatOverviewPercent(figures[PERCENT_KEYS[columnKey]]) : null;

    let content = <OverviewMoney value={value} negativeClassName={tones.negative} />;

    // A row without a budget is flagged rather than dashed, so it reads as a
    // project to look at and not as a figure that failed to load.
    if (columnKey === 'budget' && value === null && tone === 'body') {
        content = <Badge variant="warning">No budget</Badge>;
    }

    return (
        <td className={classNames('inbud-numeric-cell tw-whitespace-nowrap', { [tones.divider]: columnKey === RECORDED_DIVIDER })}>
            {content}
            {percent !== null && <div className={`tw-text-2xs ${tones.percent}`}>{percent}</div>}
        </td>
    );
}

function TimeOverviewRow({ row }) {
    return (
        <tr>
            <td className="tw-text-start tw-font-semibold">{row.name}</td>
            {FIGURE_COLUMNS.map((key) => (
                <FigureCell key={key} figures={row} columnKey={key} tone="body" />
            ))}
            <td className="tw-text-end">
                <a href={row.open_url} className="tw-font-semibold tw-text-blue hover:tw-text-blue-75">
                    {$t('Open')} &rarr;
                </a>
            </td>
        </tr>
    );
}

/**
 * The total row is whatever the server sent, never a sum of the rows above it:
 * those are one page of the shown projects, and the total covers every project
 * the filters admit, hidden ones included. Sorting is server-side for the same
 * reason — it ranks every project, not the page on screen.
 */
export default function TimeOverviewTable({ rows, total, hiddenCount, sort, direction, onSort }) {
    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <SortHeader columnKey="name" label={$t('Project')} sort={sort} direction={direction} onSort={onSort} numeric={false} />
                        {FIGURE_COLUMNS.map((key) => (
                            <SortHeader
                                key={key}
                                columnKey={key}
                                label={COLUMN_DEFINITIONS[key].label}
                                sort={sort}
                                direction={direction}
                                onSort={onSort}
                                numeric
                            >
                                <TimeOverviewInfoTip label={COLUMN_DEFINITIONS[key].label} definition={COLUMN_DEFINITIONS[key].definition} />
                            </SortHeader>
                        ))}
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={FIGURE_COLUMNS.length + 2} className="tw-text-center tw-text-charcoal-75">
                                {hiddenCount > 0 ? 'No projects have time recorded in this period.' : 'No projects match these filters.'}
                            </td>
                        </tr>
                    )}
                    {rows.map((row) => (
                        <TimeOverviewRow key={row.event_id} row={row} />
                    ))}
                </tbody>
                {total && (
                    <tfoot>
                        <tr>
                            <td className="tw-text-start">{$t('Total')}</td>
                            {FIGURE_COLUMNS.map((key) => (
                                <FigureCell key={key} figures={total} columnKey={key} tone="total" />
                            ))}
                            <td />
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
